/**
 * The Instagram brand mark — the ONE way Instagram is identified in the UI.
 *
 * Replaces MUI's flat `Instagram` glyph, which was tinted a different pink in
 * each place it appeared and read as a generic icon rather than the brand.
 * The gradient is Instagram's own (Meta brand resources); `mono` is for spots
 * that must follow the surrounding text colour, e.g. inside a filled button.
 */
import { useId } from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

interface InstagramLogoProps {
  size?: number;
  variant?: 'color' | 'mono';
  sx?: SxProps<Theme>;
  title?: string;
}

export default function InstagramLogo({ size = 16, variant = 'color', sx, title = 'Instagram' }: InstagramLogoProps) {
  // Gradient ids must be unique per instance, or the second logo on a page
  // silently paints with the first one's definition.
  const gradientId = `ig-grad-${useId().replace(/:/g, '')}`;
  const mono = variant === 'mono';
  // Colour: gradient tile with white strokes. Mono: an outline in the current
  // text colour — a filled tile would vanish inside a same-coloured button.
  const tileFill = mono ? 'none' : `url(#${gradientId})`;
  const ink = mono ? 'currentColor' : '#fff';
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      role="img"
      aria-label={title}
      sx={{ width: size, height: size, flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...sx }}
    >
      <title>{title}</title>
      {variant === 'color' && (
        <defs>
          <radialGradient id={gradientId} cx="30%" cy="107%" r="150%">
            <stop offset="0%" stopColor="#fdf497" />
            <stop offset="5%" stopColor="#fdf497" />
            <stop offset="45%" stopColor="#fd5949" />
            <stop offset="60%" stopColor="#d6249f" />
            <stop offset="90%" stopColor="#285AEB" />
          </radialGradient>
        </defs>
      )}
      {mono ? (
        <>
          <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" fill="none" stroke={ink} strokeWidth="2" />
          <circle cx="12" cy="12" r="4.2" fill="none" stroke={ink} strokeWidth="2" />
          <circle cx="17.3" cy="6.7" r="1.2" fill={ink} />
        </>
      ) : (
        <>
          <rect x="1" y="1" width="22" height="22" rx="6.5" fill={tileFill} />
          <rect x="5.4" y="5.4" width="13.2" height="13.2" rx="4" fill="none" stroke={ink} strokeWidth="1.8" />
          <circle cx="12" cy="12" r="3.1" fill="none" stroke={ink} strokeWidth="1.8" />
          <circle cx="16.6" cy="7.4" r="1.05" fill={ink} />
        </>
      )}
    </Box>
  );
}
